import React, { useState, useMemo } from "react";
import { Flex, Text, Card, Box, Badge, Select, TextField, Separator, IconButton, Spinner } from "@radix-ui/themes";
import {  MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon, StarFilledIcon, CheckIcon, Cross2Icon,
  EnvelopeClosedIcon, CalendarIcon, CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons";
import { useProfileContext } from "context";
import { useSurveyTemplates } from "hooks/bSurveys/useSurveyTemplates";
import { useSurveyResponses, useSurveyResponseStats } from "hooks/bSurveys/useSurveyResponses";

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function StarRating({ value, size = "2" }) {
  return (
    <Flex gap="1" align="center">
      {[1, 2, 3, 4, 5].map((star) => (
        <StarFilledIcon 
          key={star} 
          width={size === "2" ? 14 : 18} 
          height={size === "2" ? 14 : 18}
          style={{ color: star <= value ? '#f59e0b' : 'var(--gray-a5)' }}
        />
      ))}
      <Text size={size} color="gray" ml="1">{value}/5</Text>
    </Flex>
  );
}

function YesNoDisplay({ value }) {
  return value ? (
    <Badge color="green" variant="soft">
      <CheckIcon width={12} height={12} />
      Yes
    </Badge>
  ) : (
    <Badge color="red" variant="soft">
      <Cross2Icon width={12} height={12} />
      No
    </Badge>
  );
}

function AnswerDisplay({ answer }) {
  // Handle both API format (block_type_snapshot) and component format (block_type)
  const blockType = answer.block_type_snapshot || answer.block_type;
  const ratingValue = answer.rating_value;
  const booleanValue = answer.boolean_value;
  const textValue = answer.text_value;
  const selectedOptionText = answer.selected_option_text;
  const selectedOptionsText = answer.selected_options_text;

  switch (blockType) {
    case 'rating':
    case 'experienceRating':
      return <StarRating value={ratingValue} />;
    case 'yesNo':
    case 'recommendToFriend':
    case 'marketingConsent':
      return <YesNoDisplay value={booleanValue} />;
    case 'shortAnswer':
    case 'whatCanWeImprove':
      return textValue ? (
        <Text size="2" style={{ fontStyle: textValue ? 'normal' : 'italic' }}>
          {textValue || 'No answer provided'}
        </Text>
      ) : (
        <Text size="2" color="gray" style={{ fontStyle: 'italic' }}>No answer provided</Text>
      );
    case 'multipleChoice':
    case 'howDidYouFindUs':
      return <Badge variant="surface" color="teal">{selectedOptionText}</Badge>;
    case 'checkbox':
      return (
        <Flex gap="1" wrap="wrap">
          {selectedOptionsText?.length > 0 ? (
            selectedOptionsText.map((opt, i) => (
              <Badge key={i} variant="soft" size="1">{opt}</Badge>
            ))
          ) : (
            <Text size="2" color="gray" style={{ fontStyle: 'italic' }}>No options selected</Text>
          )}
        </Flex>
      );
    default:
      return <Text size="2" color="gray">-</Text>;
  }
}

// =============================================================================
// RESPONSE CARD COMPONENT (Expandable)
// =============================================================================

function ResponseCard({ response, isExpanded, onToggle }) {
  const formattedDate = new Date(response.completed_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Get answers array (handle both API format and component format)
  const answers = response.answers || [];

  // Get the experience rating for preview (handle both API and component format)
  const experienceAnswer = answers.find(a => 
    (a.block_type_snapshot || a.block_type) === 'experienceRating'
  );
  const recommendAnswer = answers.find(a => 
    (a.block_type_snapshot || a.block_type) === 'yesNo' || 
    (a.block_type_snapshot || a.block_type) === 'recommendToFriend'
  );

  return (
    <Card style={{ borderLeft: `3px solid ${response.template_color || '#6b7280'}`,
        cursor: 'pointer', padding: 'var(--space-4)' }}
        onClick={onToggle}>
      <Flex direction="column" gap="3">
        {/* Header Row */}
        <Flex justify="between" align="center">
          <Flex align="center" gap="3">
            <Flex direction="column" gap="1">
              <Flex align="center" gap="2">
                <EnvelopeClosedIcon />
                <Text size="3" weight="medium">{response.email}</Text>
              </Flex>
              <Flex align="center" gap="2">
                <CalendarIcon color="gray" />
                <Text size="1" color="gray">{formattedDate}</Text>
              </Flex>
            </Flex>
          </Flex>
          
          <Flex align="center" gap="4">
            {/* Quick Preview Stats */}
            {experienceAnswer && !isExpanded && (
              <Flex align="center" gap="2">
                <StarRating value={experienceAnswer.rating_value} size="1" />
              </Flex>
            )}
            {recommendAnswer && !isExpanded && (
              <YesNoDisplay value={recommendAnswer.boolean_value} />
            )}
            
            {/* Marketing Consent (always visible) */}
            <Badge variant="soft" color={response.marketing_consent ? 'green' : 'red'} size="1">
              {response.marketing_consent ? <CheckCircledIcon width="12" height="12"/> : <CrossCircledIcon width="12" height="12"/>} 
              {response.marketing_consent ? 'Opted-in' : 'Not opted-in'}
            </Badge>
            
            <Badge variant="outline" color="gray" size="1">
              {response.template_name}
            </Badge>
            
            <IconButton variant="ghost" size="1" color="gray">
              {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconButton>
          </Flex>
        </Flex>

        {/* Expanded Content */}
        {isExpanded && (
          <>
            <Separator size="4" />
            <Flex direction="column" gap="3">
              {answers.map((answer, index) => (
                <Flex key={index} direction="column" gap="1">
                  <Text size="2" color="gray" weight="medium">
                    {answer.question_snapshot || answer.question}
                  </Text>
                  <AnswerDisplay answer={answer} />
                </Flex>
              ))}
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function SurveyResponses() {
  const { userProfile } = useProfileContext();
  const organisationId = userProfile?.organisation_id;
  
  const [selectedTemplate, setSelectedTemplate] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedResponseId, setExpandedResponseId] = useState(null);

  // Fetch templates for the filter dropdown
  const { surveyTemplates, isLoading: isLoadingTemplates } = useSurveyTemplates(organisationId);
  
  // Fetch responses stats (for empty state check)
  const { 
    data: statsData, 
    isLoading: isLoadingStats 
  } = useSurveyResponseStats(
    organisationId, 
    selectedTemplate !== 'all' ? selectedTemplate : null
  );
  
  // Fetch responses list
  const { 
    data: responsesData, 
    isLoading: isLoadingResponses 
  } = useSurveyResponses(organisationId, {
    templateId: selectedTemplate !== 'all' ? selectedTemplate : null,
    search: searchQuery || null,
    limit: 200,
  });

  const responses = useMemo(() => {
    if (!responsesData?.results) return [];
    return responsesData.results;
  }, [responsesData]);

  const templates = surveyTemplates || [];
  const isLoading = isLoadingTemplates || isLoadingStats;
  const hasResponses = statsData?.total_responses > 0;

  if (isLoading && !statsData) {
    return (
      <Flex direction="column" align="center" justify="center" gap="3" style={{ width: "100%", height: "100%", padding: "40px" }}>
        <Spinner size="3" />
        <Text size="2" color="gray">Loading responses...</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center", flex: "1 0 0" }}>
      {!hasResponses ? (
        // Empty State
        <Flex 
          direction="column" 
          style={{
            justifyContent: 'center', 
            alignItems: 'center', 
            alignSelf: 'stretch',
            border: '1px dashed var(--gray-a6)', 
            borderRadius: 'var(--radius-4)', 
            height: '100%', 
            flex: '1 0 0', 
            padding: 'var(--space-6)'
          }}
        >
          <Text color="gray" size="3">No survey responses yet.</Text>
          <Text color="gray" size="2" style={{ marginTop: 'var(--space-2)' }}>
            Responses will appear here once customers start completing your surveys.
          </Text>
        </Flex>
      ) : (
        <>
          {/* Header with response count and filters */}
          <Flex justify="between" align="center" wrap="wrap" gap="3" style={{ alignSelf: 'stretch' }}>
            {/* Response count */}
            <Text size="2" color="gray">
              {isLoadingResponses ? 'Loading...' : (
                <>
                  Showing {responses.length} response{responses.length !== 1 ? 's' : ''}
                  {responsesData?.total_count > responses.length && ` of ${responsesData.total_count}`}
                  {selectedTemplate !== 'all' && ` for ${templates.find(t => t.template_id === selectedTemplate)?.name}`}
                </>
              )}
            </Text>

              <Flex gap="3" align="center">
                {/* Survey Filter */}
                <Select.Root value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <Select.Trigger placeholder="All surveys" style={{ minWidth: '180px' }} />
                  <Select.Content>
                    <Select.Item value="all">All surveys</Select.Item>
                    <Select.Separator />
                    {templates.map(template => (
                      <Select.Item key={template.template_id} value={template.template_id}>
                        <Flex align="center" gap="2">
                          <Box 
                            style={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              backgroundColor: template.primary_color 
                            }}
                          />
                          {template.name}
                        </Flex>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>

                {/* Search */}
                <TextField.Root
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="[&_input]:!pl-2"
                  style={{ width: '200px' }}
                >
                  <TextField.Slot>
                    <MagnifyingGlassIcon height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
          </Flex>

          {/* Response List - Scrollable */}
          <Flex direction="column" gap="5" style={{ alignSelf: "stretch", width: "100%", overflow: 'auto', height: '100%', flex: "1 0 0" }}>
            {isLoadingResponses ? (
              <Flex direction="column" align="center" justify="center" gap="3" style={{ height: '200px' }}>
                <Spinner size="3" />
                <Text size="2" color="gray">Loading responses...</Text>
              </Flex>
            ) : responses.length === 0 ? (
              <Flex 
                direction="column" 
                align="center" 
                justify="center" 
                style={{ 
                  height: '200px',
                  border: '1px dashed var(--gray-a6)',
                  borderRadius: 'var(--radius-4)',
                }}
              >
                <Text color="gray">No responses match your filters.</Text>
              </Flex>
            ) : (
              <Flex direction="column" gap="2">
                {responses.map(response => (
                  <ResponseCard
                    key={response.id}
                    response={response}
                    isExpanded={expandedResponseId === response.id}
                    onToggle={() => setExpandedResponseId(
                      expandedResponseId === response.id ? null : response.id
                    )}
                  />
                ))}
              </Flex>
            )}
          </Flex>
        </>
      )}
    </Flex>
  );
}
